import React, { useLayoutEffect, useRef, useState } from 'react';

interface ExpandableDescriptionProps {
  text: string;
  /** Number of lines to show when collapsed. Default 2. */
  clampLines?: number;
  className?: string;
}

/**
 * Renders a description with a line clamp. If the content actually overflows
 * past `clampLines`, a Show more / Show less toggle is rendered. For short
 * descriptions that fit, no toggle appears.
 *
 * Overflow detection measures `scrollHeight > clientHeight` after layout and
 * re-measures on text changes + window resizes (in case the container width
 * changes and a description that used to fit now wraps).
 */
export const ExpandableDescription: React.FC<ExpandableDescriptionProps> = ({
  text,
  clampLines = 2,
  className = '',
}) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      // Always measure against the unclamped full height, then compare to
      // what `clampLines` lines would take up. Using clientHeight alone breaks
      // when the component is already expanded (no clamp is applied, so
      // clientHeight == full height and the overflow check falsely reports
      // "fits" — which would hide the "Show less" toggle and trap the user).
      const prevClamp = el.style.webkitLineClamp;
      const prevDisplay = el.style.display;
      const prevOverflow = el.style.overflow;
      el.style.webkitLineClamp = 'unset';
      el.style.display = 'block';
      el.style.overflow = 'visible';
      const fullHeight = el.scrollHeight;
      el.style.webkitLineClamp = prevClamp;
      el.style.display = prevDisplay;
      el.style.overflow = prevOverflow;

      const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight);
      if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
        // Fallback: if we can't parse the line-height (e.g. "normal"), assume
        // overflow when we have >2× the text of a single-line assumption.
        setOverflows(fullHeight > 40);
        return;
      }
      const clampThresholdPx = lineHeight * clampLines;
      setOverflows(fullHeight > clampThresholdPx + 1);
    };

    check();

    // Re-check on container resize (e.g. mobile/desktop breakpoint flip).
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [text, clampLines, expanded]);

  if (!text || text.trim().length === 0) return null;

  return (
    <div className={`flex w-full flex-col gap-1 ${className}`}>
      <p
        ref={ref}
        className="text-caption font-caption text-subtext-color whitespace-pre-wrap break-words"
        style={
          expanded
            ? undefined
            : {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: clampLines,
                overflow: 'hidden',
              }
        }
      >
        {text}
      </p>
      {overflows && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="self-start text-caption-bold font-caption-bold text-brand-700 hover:text-brand-600 transition-colors"
        >
          Show more
        </button>
      )}
    </div>
  );
};
